// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { OctoRuleOutcome, OctoRuleStage } from "@/api/hooks/use-octo-rules";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const STAGE_CLASS: Record<OctoRuleStage, string> = {
  creation: "bg-[#E3F1FF] border-blue-200 text-foreground dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
  visibility:
    "bg-[#F3E8FF] border-purple-200 text-foreground dark:bg-purple-950 dark:text-purple-300 dark:border-purple-900",
  workability:
    "bg-[#FFF5D1] border-amber-200 text-foreground dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
};

const OUTCOME_CLASS: Record<OctoRuleOutcome, string> = {
  allow: "bg-[#E6F4EA] border-green-200 text-foreground dark:bg-green-950 dark:text-green-300 dark:border-green-900",
  deny: "bg-[#FDE8E8] border-red-200 text-foreground dark:bg-red-950 dark:text-red-300 dark:border-red-900",
};

export function OctoStageBadge({ stage, className }: { stage: OctoRuleStage; className?: string }) {
  const { t } = useTranslation("back-office");
  return (
    <Badge variant="secondary" className={cn("font-normal lowercase", STAGE_CLASS[stage], className)}>
      {t(`octoRules.stage.${stage}`)}
    </Badge>
  );
}

export function OctoOutcomeBadge({ outcome, className }: { outcome: OctoRuleOutcome; className?: string }) {
  const { t } = useTranslation("back-office");
  return (
    <Badge variant="secondary" className={cn("font-normal lowercase", OUTCOME_CLASS[outcome], className)}>
      {t(`octoRules.outcome.${outcome}`)}
    </Badge>
  );
}
